/**
 * Which yards have a nesting bird, and where its nest sits.
 *
 * Only Hubert today. Yards absent from this map simply have no bird.
 */

import type { BirdYardConfig } from '@/world-prototype/creatures/birdBehavior';
import type { PresenceSpace } from '@/world-prototype/types';

/**
 * Scenery id of the nest prop.
 *
 * The nest lives in the yard's nature-scatter pack, not with the bird, so it
 * can be dragged and resized in the World Editor like the pinecones and the
 * fire pit. The bird looks the placed layer up by this id and guards wherever
 * the nest actually ended up.
 */
export const BIRD_NEST_OBJECT_ID = 'bird-nest';

/**
 * Hubert — the nest is tucked on the grass just below the little pine, on the
 * quiet left side of the yard.
 *
 * Chosen against the resolved prop layout, not by eye: every interactive stand
 * point in the yard (all five pinecones, the bucket, the shed, the hammock, the
 * fire pit, the garden bed) sits outside the bird's protect ring, so guarding
 * the nest can never fight with the pinecone-and-bucket loop. The spot is still
 * inside the player's walk polygon, which matters — the joke needs the child to
 * be able to walk right up and get told off.
 *
 * The roam polygon is the bird's own and is deliberately much smaller than the
 * rabbit's: it only has to cover the nest, the pottering ring, and enough lawn
 * to charge an intruder standing at the protect ring.
 */
const HUBERT_BIRD: BirdYardConfig = {
  yardId: 'hubert',
  nest: { x: 0.25, y: 0.505 },
  /**
   * A real plover scrape is about a foot across. Sized well past that so the
   * eggs are legible from normal play distance — a child has to be able to see
   * what the bird is making all the fuss about.
   */
  /**
   * Big enough to run an intruder all the way off the lawn — the bird's leash
   * is what limits the chase, not the polygon. Same inset discipline as the
   * rabbit's: it stops short of the painted fences, which have no occlusion in
   * the master plate and would otherwise be drawn over.
   */
  roamPolygon: [
    { x: 0.235, y: 0.47 },
    { x: 0.42, y: 0.46 },
    { x: 0.7, y: 0.46 },
    { x: 0.8, y: 0.56 },
    { x: 0.88, y: 0.66 },
    { x: 0.9, y: 0.78 },
    { x: 0.14, y: 0.8 },
    { x: 0.13, y: 0.7 },
    { x: 0.19, y: 0.6 },
    { x: 0.215, y: 0.53 },
  ],
};

const BIRD_YARDS: Readonly<Record<string, BirdYardConfig>> = {
  hubert: HUBERT_BIRD,
};

/** Bird config for a yard, or null when that yard has no bird. */
export function birdConfigForYard(
  yardId: string | null | undefined,
): BirdYardConfig | null {
  if (!yardId) return null;
  return BIRD_YARDS[yardId] ?? null;
}

/** Yard ids that currently host a bird. */
export function birdYardIds(): readonly string[] {
  return Object.keys(BIRD_YARDS);
}

/**
 * Config with the nest moved to wherever the editor placed it.
 *
 * Falls back to the authored coordinates when the scenery layer is missing, so
 * a disabled or not-yet-loaded nest still leaves the bird with a sane anchor.
 */
export function resolveBirdConfig(
  config: BirdYardConfig,
  space: PresenceSpace,
): BirdYardConfig {
  const layer = space.scenery?.layers?.find(
    (candidate) => candidate.id === BIRD_NEST_OBJECT_ID,
  );
  if (!layer || layer.x == null || layer.y == null) return config;
  // Called every frame: hand back the same object when nothing moved.
  if (config.nest.x === layer.x && config.nest.y === layer.y) return config;
  return { ...config, nest: { x: layer.x, y: layer.y } };
}
